import Avatar from "../components/UserAvatar";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ref, update } from "firebase/database";
import {
  LuSearch,
  LuX,
  LuZap,
  LuCompass,
  LuRocket,
  LuActivity,
  LuUsers,
  LuUserPlus,
  LuCheck,
} from "react-icons/lu";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { useRecord } from "../utils/appData";
import WorkShell, { State } from "../components/AppPage";
import {
  PAGE_SIZE,
  activeNow,
  discoverModel,
  displayName,
  friendStatus,
  friendshipPatch,
  mutualCount,
  nicheLabel,
  profileNiches,
  timestamp,
} from "../utils/discoverData";

function Heading({ icon: Icon, title, children }) {
  return (
    <div className="mb-2.5 flex items-center justify-between gap-2 [&>span]:shrink-0 [&>span]:text-xs [&>span]:text-slate-500 dark:[&>span]:text-slate-400">
      <h2 className="flex min-w-0 items-center gap-1.5 text-[17px] font-bold leading-[22px]">
        <Icon className="shrink-0 text-[#22b8d1]" size={14} />
        {title}
      </h2>
      {children}
    </div>
  );
}
function FriendButton({ person, me, myId, busy, onAction, small = false }) {
  const status = friendStatus(me, person, myId);
  return (
    <button
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-full border font-bold disabled:cursor-wait disabled:opacity-60 ${small ? "mt-1 h-6 w-full gap-0.5 px-1 text-[9px]" : "h-[30px] min-w-[84px] gap-1 border-[1.5px] px-2.5 text-[11px]"} ${{ none: "border-[#327691] bg-transparent text-[#327691] dark:text-[#5FAFCB]", friends: "border-green-500 bg-green-500/[0.15] text-green-700 dark:text-green-400", sent: "border-transparent bg-gray-200 text-slate-500 dark:bg-[#333] dark:text-slate-300", received: "border-green-500 bg-green-500 text-green-950" }[status]}`}
      data-status={status}
      disabled={busy}
      onClick={() => onAction(person)}
      aria-label={`${status === "friends" ? "Remove friend" : status === "sent" ? "Cancel request to" : status === "received" ? "Accept request from" : "Add friend"} ${displayName(person)}`}
    >
      {status === "friends" ? <LuCheck size={11} /> : <LuUserPlus size={11} />}
      {busy
        ? "Saving…"
        : {
          friends: "Friends",
          sent: "Requested",
          received: "Accept",
          none: "Add Friend",
        }[status]}
    </button>
  );
}
function PersonRow({ person, me, myId, busy, onAction, now }) {
  const isNew = timestamp(person.createdAt) > now - 30 * 24 * 60 * 60 * 1000;
  return (
    <article className="flex min-h-[62px] min-w-0 items-center justify-between gap-2.5 rounded-[14px] border border-slate-200 bg-white p-2.5 shadow-[0_1px_3px_#0000000f] dark:border-[#363e46] dark:bg-[#23272c] dark:shadow-none">
      <Link
        to={`/user-profile/${person.id}`}
        className="flex min-w-0 flex-1 items-center gap-2.5"
      >
        <span className="relative inline-flex shrink-0">
          <Avatar
            user={person}
            className="h-[34px] w-[34px] shrink-0 rounded-full object-cover"
          />
          {activeNow(person, now) && (
            <i className="absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full border-[1.5px] border-white bg-emerald-500 dark:border-[#1a1f2a]" />
          )}
        </span>
        <span className="min-w-0 flex-1 [&>small]:mt-0.5 [&>small]:block [&>small]:truncate [&>small]:text-xs [&>small]:leading-4 [&>small]:text-slate-500 dark:[&>small]:text-slate-400">
          <span className="flex min-w-0 items-center gap-1.5 [&>strong]:min-w-0 [&>strong]:truncate [&>strong]:text-base [&>strong]:font-bold [&>strong]:leading-5">
            <strong>{displayName(person)}</strong>
            {isNew && (
              <b className="shrink-0 rounded-lg bg-blue-700 px-1.5 text-[9px] font-bold uppercase leading-4 text-white">
                New
              </b>
            )}
          </span>
          <small>
            {profileNiches(person)[0] ||
              nicheLabel(person.niche) ||
              person.role ||
              "Creator"}{" "}
            · {mutualCount(me.friends, person)} mutuals
          </small>
        </span>
      </Link>
      <div className="flex shrink-0 flex-col items-stretch gap-1.5">
        <FriendButton {...{ person, me, myId, busy, onAction }} />
        <Link
          to={`/chat/${person.id}`}
          className="inline-flex h-[30px] min-w-[84px] items-center justify-center rounded-full bg-[#4b9ab6] px-3 text-[13px] font-bold leading-4 text-white"
        >
          Say Hi 👋
        </Link>
      </div>
    </article>
  );
}
export default function Discover() {
  const { currentUser } = useUser();
  const uid = currentUser?.uid;
  const state = useRecord(uid ? "Users" : null);
  const [search, setSearch] = useState("");
  const [niche, setNiche] = useState("All");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [busy, setBusy] = useState({});
  const pending = useRef(new Set());
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [friendSearch, setFriendSearch] = useState("");
  const dialogRef = useRef(null);
  const sentinelRef = useRef(null);
  const model = useMemo(
    () => discoverModel(state.data, uid, niche, search, now),
    [state.data, uid, niche, search, now],
  );
  const searching = Boolean(search.trim());
  const all = searching ? model.pool : model.ranked;
  const shown = all.slice(0, visible);
  const hasMore = shown.length < all.length;
  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [niche, search, uid]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);
  const loadMore = useCallback(
    () =>
      setVisible((count) =>
        Math.min(count + PAGE_SIZE, Math.max(all.length, PAGE_SIZE)),
      ),
    [all.length],
  );
  useEffect(() => {
    if (
      !hasMore ||
      !sentinelRef.current ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      {
        root: sentinelRef.current.closest("main"),
        rootMargin: "0px 0px 120px 0px",
      },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadMore, visible]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (friendsOpen && !dialog.open) dialog.showModal();
    else if (!friendsOpen && dialog.open) dialog.close();
    if (!friendsOpen) return;
    const main = dialog.closest("main") || document.querySelector(".zy-main");
    const previousOverflow = main?.style.overflow;
    if (main) main.style.overflow = "hidden";
    return () => {
      if (main) main.style.overflow = previousOverflow;
    };
  }, [friendsOpen]);
  const openFriends = useCallback(() => {
    setFriendSearch("");
    setFriendsOpen(true);
  }, []);
  const headerActions = useMemo(
    () => (
      <button
        className="inline-flex h-[34px] items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-[#327691] px-[13px] text-xs font-bold text-white"
        onClick={openFriends}
      >
        <LuUsers size={14} />
        Friends ({model.friends.length})
      </button>
    ),
    [openFriends, model.friends.length],
  );
  async function act(person) {
    if (!uid || pending.current.has(person.id)) return;
    pending.current.add(person.id);
    setBusy((prev) => ({ ...prev, [person.id]: true }));
    setError("");
    try {
      await update(
        ref(database),
        friendshipPatch(uid, person.id, friendStatus(model.me, person, uid)),
      );
    } catch (e) {
      setError(
        e.message || "Could not update this friendship. Please try again.",
      );
    } finally {
      pending.current.delete(person.id);
      setBusy((prev) => ({ ...prev, [person.id]: false }));
    }
  }
  const personRow = (person) => (
    <PersonRow
      key={person.id}
      person={person}
      me={model.me}
      myId={uid}
      busy={busy[person.id]}
      onAction={act}
      now={now}
    />
  );
  const friendList = model.friends.filter((u) =>
    [displayName(u), ...profileNiches(u)]
      .join(" ")
      .toLowerCase()
      .includes(friendSearch.trim().toLowerCase()),
  );
  return (
    <WorkShell title="Discover" headerActions={headerActions}>
      <div className="w-full min-w-0 text-[#1a1a1a] dark:text-slate-50">
        <label className="mb-5 flex h-12 items-center gap-2.5 rounded-2xl border border-slate-200 bg-[#F5F9FE] px-3.5 text-slate-500 dark:border-[#363e46] dark:bg-[#23272c] dark:text-slate-400 [&>input]:min-w-0 [&>input]:flex-1 [&>input]:border-0 [&>input]:bg-transparent [&>input]:text-sm [&>input]:text-[#1a1a1a] [&>input]:outline-none [&>input:focus-visible]:outline-none dark:[&>input]:text-slate-50 [&>button]:flex [&>button]:h-8 [&>button]:w-8 [&>button]:shrink-0 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full">
          <LuSearch size={20} />
          <input
            type="text"
            placeholder="Search people, niches..."
            aria-label="Search people and niches"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              aria-label="Clear people search"
              onClick={() => setSearch("")}
            >
              <LuX size={18} />
            </button>
          )}
        </label>
        <State loading={state.loading} error={state.error} />
        {error && !friendsOpen && (
          <p
            role="alert"
            className="my-3 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {error}
          </p>
        )}
        {!state.loading && !state.error && (
          <>
            {searching ? (
              <section className="mb-5 min-w-0">
                <Heading icon={LuSearch} title="Search Results">
                  <span>
                    {hasMore ? `${shown.length} of ${all.length}` : all.length}{" "}
                    people
                  </span>
                </Heading>
                {!all.length && (
                  <p className="px-3 py-6 text-center text-[13px] text-slate-500 dark:text-slate-400">
                    No people found. Try another name or niche.
                  </p>
                )}
                <div className="grid grid-cols-1 gap-2.5 min-[1200px]:grid-cols-2">
                  {shown.map(personRow)}
                </div>
              </section>
            ) : (
              <>
                <section className="mb-5 min-w-0">
                  <Heading icon={LuZap} title="Friends Online">
                    <i className="mr-auto h-[7px] w-[7px] shrink-0 rounded-full bg-green-500" />
                  </Heading>
                  {model.online.length ? (
                    <div className="flex gap-2.5 overflow-x-auto overscroll-x-contain pb-2 pr-1 pt-0.5">
                      {model.online.map((person) => (
                        <Link
                          key={person.id}
                          to={`/user-profile/${person.id}`}
                          className="flex w-14 shrink-0 flex-col items-center gap-1.5 [&>span:last-child]:w-full [&>span:last-child]:truncate [&>span:last-child]:text-center [&>span:last-child]:text-[11px] [&>span:last-child]:text-slate-500 dark:[&>span:last-child]:text-slate-400"
                        >
                          <span className="relative inline-flex shrink-0">
                            <Avatar
                              user={person}
                              className="h-[50px] w-[50px] shrink-0 rounded-full border border-[#2c4d6f] object-cover"
                            />
                            <i className="absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full border-[1.5px] border-white bg-emerald-500 dark:border-[#1a1f2a]" />
                          </span>
                          <span>{displayName(person)}</span>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="py-3 text-left text-[13px] text-slate-500 dark:text-slate-400">
                      No active friends right now.
                    </p>
                  )}
                </section>
                <section className="mb-5 min-w-0">
                  <Heading icon={LuCompass} title="Browse by Niche" />
                  <div
                    className="flex gap-2 overflow-x-auto overscroll-x-contain pb-2 pr-1 pt-0.5 [&>button]:h-[34px] [&>button]:shrink-0 [&>button]:whitespace-nowrap [&>button]:rounded-xl [&>button]:border [&>button]:border-slate-200 [&>button]:bg-white [&>button]:px-3 [&>button]:text-xs [&>button]:font-semibold [&>button]:text-slate-500 dark:[&>button]:border-slate-700 dark:[&>button]:bg-[#1a1f2a] dark:[&>button]:text-slate-400 [&>button[aria-pressed=true]]:border-[#327691] [&>button[aria-pressed=true]]:bg-[#327691] [&>button[aria-pressed=true]]:text-white dark:[&>button[aria-pressed=true]]:border-[#327691] dark:[&>button[aria-pressed=true]]:bg-[#327691] dark:[&>button[aria-pressed=true]]:text-white"
                    aria-label="Browse by niche"
                  >
                    {model.chips.map((chip) => (
                      <button
                        key={chip.label}
                        aria-pressed={niche === chip.label}
                        onClick={() => setNiche(chip.label)}
                      >
                        {chip.label} ({chip.count})
                      </button>
                    ))}
                  </div>
                </section>
                <section className="mb-5 min-w-0">
                  <Heading icon={LuRocket} title="New on zylen" />
                  {model.newest.length ? (
                    <div className="flex gap-2.5 overflow-x-auto overscroll-x-contain pb-2 pr-3 pt-0.5">
                      {model.newest.map((person) => (
                        <article
                          className="w-[106px] shrink-0 rounded-xl border border-slate-200 bg-white p-2.5 text-center dark:border-[#363e46] dark:bg-[#23272c] [&>a:first-child]:flex [&>a:first-child]:min-w-0 [&>a:first-child]:flex-col [&>a:first-child]:items-center [&_strong]:mb-0.5 [&_strong]:block [&_strong]:w-full [&_strong]:truncate [&_strong]:text-xs [&_strong]:font-bold [&_small]:mb-1.5 [&_small]:block [&_small]:w-full [&_small]:truncate [&_small]:text-[10px] [&_small]:text-slate-500 dark:[&_small]:text-slate-400"
                          key={person.id}
                        >
                          <Link to={`/user-profile/${person.id}`}>
                            <Avatar
                              user={person}
                              className="mb-2 h-11 w-11 shrink-0 rounded-full object-cover"
                            />
                            <strong>{displayName(person)}</strong>
                            <small>
                              {profileNiches(person)[0] ||
                                nicheLabel(person.niche) ||
                                person.role ||
                                "Creator"}
                            </small>
                          </Link>
                          <Link
                            to={`/chat/${person.id}`}
                            className="inline-flex h-6 w-full items-center justify-center rounded-full bg-[#DBE7F8] px-1 text-[10px] font-bold text-[#17324F] dark:bg-[#262f3c] dark:text-white"
                          >
                            👋 Say Hi
                          </Link>
                          <FriendButton
                            person={person}
                            me={model.me}
                            myId={uid}
                            busy={busy[person.id]}
                            onAction={act}
                            small
                          />
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="py-3 text-left text-[13px] text-slate-500 dark:text-slate-400">
                      No creators in this niche yet.
                    </p>
                  )}
                </section>
                <section className="mb-5 min-w-0">
                  <Heading icon={LuActivity} title="Discover Creators">
                    <span>
                      {hasMore
                        ? `${shown.length} of ${all.length}`
                        : all.length}{" "}
                      people
                    </span>
                  </Heading>
                  <div className="grid grid-cols-1 gap-2.5 min-[1200px]:grid-cols-2">
                    {shown.map(personRow)}
                  </div>
                  {!all.length && (
                    <p className="px-3 py-6 text-center text-[13px] text-slate-500 dark:text-slate-400">
                      No creators in this niche yet. Try All.
                    </p>
                  )}
                </section>
              </>
            )}
            {hasMore && (
              <div ref={sentinelRef}>
                <button
                  className="flex w-full items-center justify-center rounded-[14px] border border-slate-200 bg-white py-3 text-[13px] font-bold text-[#327691] dark:border-[#363e46] dark:bg-[#23272c] dark:text-[#5FAFCB]"
                  onClick={loadMore}
                >
                  Show more ({all.length - shown.length} left)
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <dialog
        ref={dialogRef}
        className="fixed inset-0 m-0 h-[100dvh] max-h-none w-screen max-w-none overflow-hidden border-0 bg-transparent p-0 text-[#1a1a1a] backdrop:bg-black/50 open:flex open:items-end dark:text-slate-50 md:p-6 md:open:items-center md:open:justify-center"
        onCancel={() => setFriendsOpen(false)}
        onClose={() => setFriendsOpen(false)}
        onClick={(e) => {
          if (e.target === e.currentTarget) setFriendsOpen(false);
        }}
        aria-labelledby="discover-friends-title"
      >
        <div className="flex h-[80dvh] max-h-[calc(100dvh_-_16px)] min-h-0 w-full flex-col overflow-hidden rounded-t-[32px] bg-white shadow-xl dark:bg-[#23272c] md:h-[min(720px,85dvh)] md:max-w-[620px] md:rounded-3xl">
          <div className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-700">
            <h2 id="discover-friends-title" className="text-xl font-extrabold">
              Friends ({model.friends.length})
            </h2>
            <button
              autoFocus
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#5FAFCB] dark:text-slate-300 dark:hover:bg-slate-800"
              aria-label="Close friends"
              onClick={() => setFriendsOpen(false)}
            >
              <LuX size={24} />
            </button>
          </div>
          <label className="mx-4 my-4 flex h-11 shrink-0 items-center gap-2.5 rounded-xl border border-slate-200 bg-[#F5F9FE] px-3 text-slate-500 focus-within:ring-2 focus-within:ring-[#5FAFCB] dark:border-slate-700 dark:bg-[#262626] dark:text-slate-400">
            <LuSearch className="shrink-0" size={18} />
            <input
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-[#1a1a1a] outline-none focus-visible:outline-none dark:text-white"
              aria-label="Search friends"
              placeholder="Search friends..."
              value={friendSearch}
              onChange={(e) => setFriendSearch(e.target.value)}
            />
          </label>
          {error && friendsOpen && (
            <p
              role="alert"
              className="mx-4 mb-3 shrink-0 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200"
            >
              {error}
            </p>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">
            {friendList.map((person) => (
              <Link
                key={person.id}
                to={`/user-profile/${person.id}`}
                onClick={() => setFriendsOpen(false)}
                className="mb-2 flex min-w-0 items-center gap-4 rounded-2xl p-3 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <Avatar
                  user={person}
                  className="h-[52px] w-[52px] shrink-0 rounded-full object-cover"
                />
                <span className="min-w-0 flex-1">
                  <strong
                    className={`block break-words text-base font-bold ${person.role === "Host" ? "text-[#B8860B] dark:text-[#EFBF04]" : ""}`}
                  >
                    {displayName(person)}
                  </strong>
                  <small className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                    {profileNiches(person)[0] ||
                      nicheLabel(person.niche) ||
                      "No niche specified"}
                  </small>
                </span>
              </Link>
            ))}
            {!friendList.length && (
              <p className="px-3 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                {model.friends.length
                  ? "No matching friends."
                  : "Your friends will appear here."}
              </p>
            )}
          </div>
          <div className="shrink-0 border-t border-slate-200 px-6 pt-4 pb-[max(20px,env(safe-area-inset-bottom))] text-center text-[13px] font-semibold text-slate-500 dark:border-slate-700 dark:text-slate-400">
            {model.friends.length}{" "}
            {model.friends.length === 1 ? "friend" : "friends"}
          </div>
        </div>
      </dialog>
    </WorkShell>
  );
}
